import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import type { PublishProjectInput } from "@rec/shared";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";

/**
 * The publish decision, stated before it costs anything.
 *
 * Two things the person must understand here and nowhere else:
 *
 *  - An unedited video with authored chapters replaces the chapters on the
 *    source recording — the previous set stays restorable in version history.
 *  - An edited video renders a **new** recording, and they choose what happens
 *    to its transcript. Carrying it through the cuts is exact and free, so it
 *    is the default; re-transcribing spends ElevenLabs money and is only
 *    worth it when sources had no transcript to carry.
 */
export function PublishDialog({
  defaultTitle,
  isIdentity,
  hasChapters,
  busy,
  onPublish,
  onClose,
}: {
  defaultTitle: string;
  /** True when the video is untouched and only chapters would publish. */
  isIdentity: boolean;
  hasChapters: boolean;
  busy: boolean;
  onPublish: (input: PublishProjectInput) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(["studio", "common"]);
  const [title, setTitle] = useState(defaultTitle);
  const [mode, setMode] = useState<PublishProjectInput["transcriptMode"]>("map");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const chaptersOnly = isIdentity && hasChapters;

  const modes: Array<{
    id: PublishProjectInput["transcriptMode"];
    label: string;
    detail: string;
  }> = [
    {
      id: "map",
      label: t("publish.modes.map.label"),
      detail: t("publish.modes.map.detail"),
    },
    {
      id: "asr",
      label: t("publish.modes.asr.label"),
      detail: t("publish.modes.asr.detail"),
    },
    {
      id: "none",
      label: t("publish.modes.none.label"),
      detail: t("publish.modes.none.detail"),
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("editor.publish")}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      {/* max-h + internal scroll: in phone landscape this card is taller than
          the viewport, and a centred fixed overlay clips both ends — Publish
          and Cancel were unreachable. */}
      <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-xl border border-border bg-surface p-5 shadow-[var(--shadow-md)]">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-[16px] font-semibold text-text">
            {chaptersOnly ? t("publish.titleChapters") : t("publish.titleEdit")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded p-1 text-text-faint hover:bg-surface-hover hover:text-text"
            aria-label={t("common:actions.close")}
          >
            <X size={16} />
          </button>
        </div>

        {chaptersOnly ? (
          <p className="mt-3 text-[13.5px] leading-relaxed text-text-muted">
            {t("publish.chaptersOnlyBody")}
          </p>
        ) : (
          <>
            <p className="mt-3 text-[13.5px] leading-relaxed text-text-muted">
              <Trans
                t={t}
                i18nKey="publish.renderBody"
                components={{ strong: <strong className="text-text" /> }}
              />
            </p>

            <label className="mt-4 block">
              <span className="text-[12px] font-medium text-text-muted">{t("publish.titleLabel")}</span>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                dir="auto"
                className={cn(
                  "mt-1 w-full rounded-lg border border-border bg-bg px-3 py-2",
                  "text-[14px] text-text focus:border-accent focus:outline-none",
                )}
              />
            </label>

            <fieldset className="mt-4 space-y-2">
              <legend className="text-[12px] font-medium text-text-muted">
                {t("publish.transcriptLegend")}
              </legend>
              {modes.map((option) => (
                <label
                  key={option.id}
                  className={cn(
                    "block cursor-pointer rounded-lg border p-3 transition-colors",
                    mode === option.id
                      ? "border-accent bg-accent-subtle/30"
                      : "border-border hover:border-border-strong",
                  )}
                >
                  <span className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="transcript-mode"
                      checked={mode === option.id}
                      onChange={() => setMode(option.id)}
                    />
                    <span className="text-[13.5px] font-medium text-text">
                      {option.label}
                    </span>
                  </span>
                  <span className="mt-1 block pl-6 text-[12.5px] leading-relaxed text-text-muted">
                    {option.detail}
                  </span>
                </label>
              ))}
            </fieldset>
          </>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            {t("common:actions.cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busy || (!chaptersOnly && title.trim().length === 0)}
            onClick={() =>
              onPublish({
                title: title.trim() || defaultTitle,
                transcriptMode: chaptersOnly ? "map" : mode,
              })
            }
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : null}
            {chaptersOnly ? t("publish.titleChapters") : t("editor.publish")}
          </Button>
        </div>
      </div>
    </div>
  );
}
